/**
 * Canonical partners dataset for Fenix Srl.
 *
 * The sales & distribution network is grouped by country. Each country has
 * an ordered list of partner entries. All user-facing strings (country names,
 * partner roles, optional cities) are resolved via i18next under
 * `partners.countries.*` and `partners.entries.{key}.*`. Partner company names
 * are intentionally stored here as raw brand strings — they are proper nouns
 * and do not need translation.
 *
 * Slugs used as translation-key suffixes are camelCase to match the rest of
 * the project's translation-key conventions.
 */

export type PartnerCountryCode = 'AT' | 'IT' | 'CH'

export interface PartnerEntry {
  /** Stable camelCase key used to fetch translations. */
  key: string
  /** Proper-noun partner/brand name — rendered as-is. */
  name: string
  /** Optional external website URL. */
  website?: string
  /**
   * Optional logo asset, served from `public/images/partners/`. Omit for
   * partners whose brand mark we don't have locally.
   */
  logo?: string
  /**
   * When true, translation keys `partners.entries.{key}.city` and
   * `partners.entries.{key}.role` are expected to exist. When false, only
   * the raw `name` is displayed.
   */
  hasMeta?: boolean
}

export interface PartnerCountry {
  /** ISO 3166-1 alpha-2 code — used for the typographic "flag" chip. */
  code: PartnerCountryCode
  /** Translation key used under `partners.countries.*`. */
  translationKey: 'austria' | 'italy' | 'switzerland'
  /** Ordered list of partner entries for this country. */
  entries: PartnerEntry[]
}

/**
 * The Fenix Srl partner / distribution network, grouped by country.
 *
 * Extracted from the original fenixgrind.it partner page:
 * - Austria → Schierhofer
 * - Italia  → Essig Utensili
 * - Svizzera → hstech
 */
export const partnerCountries: PartnerCountry[] = [
  {
    code: 'AT',
    translationKey: 'austria',
    entries: [
      {
        key: 'schierhofer',
        name: 'Schierhofer',
        website: 'https://www.schierhofer.at/',
        logo: '/images/partners/schirnhofer.png',
        hasMeta: true,
      },
    ],
  },
  {
    code: 'IT',
    translationKey: 'italy',
    entries: [
      {
        key: 'essig',
        name: 'Essig Utensili',
        website: 'https://www.essigutensili.it/',
        logo: '/images/partners/essig.jpg',
        hasMeta: true,
      },
    ],
  },
  {
    code: 'CH',
    translationKey: 'switzerland',
    entries: [
      {
        key: 'hstech',
        name: 'hstech',
        website: 'https://www.hstech.ch/',
        logo: '/images/partners/hstech.jpg',
        hasMeta: true,
      },
    ],
  },
]

/** Returns the fully qualified translation key for a given partner entry field. */
export function partnerEntryKey(
  entry: PartnerEntry,
  field: 'role' | 'city' | 'description'
): string {
  return `partners.entries.${entry.key}.${field}`
}

/** Returns the fully qualified translation key for a country label. */
export function partnerCountryKey(country: PartnerCountry): string {
  return `partners.countries.${country.translationKey}`
}
